import { shallowRef, triggerRef, watch, computed } from 'vue'
import { useClasses } from '@/modules/Classes/useClasses'
import { useOntology } from '@/modules/Classes/useOntology'
import { defineStore } from 'pinia'
import { LoadingStatus } from '@/store/types'
import { setContext } from '@/services/sentry'

const BATCH_SIZE = 1000 // process 1000 items at a time

type MentionItem = {
  label: string
  value: string
  type: 'class' | 'property' | 'property-value'
  color: string
}

/*
 * The purpose of this store is to build and provide a shared list of mentionable items
 * into the instruction prompt (using `/mention` syntax):
 * - we collect all classes
 * - for each class, we collect all properties
 * - for each property, we collect all possible values
 *
 * Depending on the team, that list can be very large, so the challenge is to build
 * the list in reasonable time. We are aware of cases with more than 100k items
 * being produced. This requires a careful approach to avoid blocking the main thread.
 */
export const useInstructionMentionsStore = defineStore('InstructionMentions', () => {
  const classStore = useClasses()
  const ontology = useOntology()

  let abortController: AbortController | null = null
  let processedItems: MentionItem[] = []
  let processedCount = 0

  const items = shallowRef<MentionItem[]>([])

  const buildMentionsAsync = async (): Promise<void> => {
    abortController = new AbortController()
    const { signal } = abortController

    const checkAndYield = async (): Promise<void> => {
      if (processedCount % BATCH_SIZE === 0) {
        // update the reactive ref with current items
        items.value = [...processedItems]
        triggerRef(items)

        // yield control to prevent blocking the main thread
        await new Promise((resolve) => {
          requestIdleCallback(resolve)
        })
      }
    }

    for (const classData of classStore.teamClasses) {
      signal.throwIfAborted()

      // add class item
      processedItems.push({
        label: classData.name,
        value: classData.name,
        type: 'class',
        color: classData.metadata._color,
      })
      processedCount++
      await checkAndYield()

      const classProperties = ontology.getClassProperties(classData)
      for (const property of classProperties) {
        signal.throwIfAborted()

        // add property item
        processedItems.push({
          label: classData.name + '/' + property.name,
          value: property.name,
          type: 'property',
          color: classData.metadata._color,
        })
        processedCount++
        await checkAndYield()

        for (const value of property.values) {
          signal.throwIfAborted()

          // add property value item
          processedItems.push({
            label: classData.name + '/' + property.name + '/' + value.name,
            value: value.name,
            type: 'property-value',
            color: classData.metadata._color,
          })
          processedCount++
          await checkAndYield()
        }
      }
    }

    signal.throwIfAborted()
    items.value = [...processedItems]
    triggerRef(items)
  }

  const isDataLoaded = computed(
    () => classStore.loadingStatus === LoadingStatus.Loaded && ontology.allLoaded,
  )

  watch(
    isDataLoaded,
    (isDataLoaded) => {
      if (isDataLoaded) {
        buildMentionsAsync().catch((error) => {
          if (error.name !== 'AbortError') {
            setContext('useInstructionMentionsStore', { error })
          }
        })
      }
    },
    { immediate: true },
  )

  const noResultsText = computed(() => {
    if (!isDataLoaded.value) {
      return 'Loading classes and properties...'
    }

    return 'No results found'
  })

  const resetState = (): void => {
    abortController?.abort()
    abortController = null

    processedItems = []
    processedCount = 0

    items.value = []
    triggerRef(items)
  }

  return { items, noResultsText, resetState }
})
